import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { useMemo, useState } from 'react';

import Button from '~/shared/components/ButtonsV2';
import Typography from '~/shared/components/Typography';
import Chevron from '~/shared/assets/icons/chevron.svg';
import { toaster } from '~/shared/utils/toaster';
import { useAuthContext } from '~/shared/context/useAuthContext';
import { Account } from '~/shared/utils/types';

import { type AutoscaleRulesBuilderAPI } from './useAutoscaleRulesBuilder';
import CreateAutoscaleRulesTables from './CreateAutoscaleRulesTable/CreateAutoscaleRulesTables';
import AutoscaleRulesBuilder from './AutoscaleRulesBuilder';
import { useCreateAutoscalingRule } from './CreateAutoscaleRulesTable/hooks/useCreateAutoscalingRule';
import AccountInputMultiSelect from './AccountInputMultiSelect';

export default function CreateAutoscaleRulesModal({
  isOpen,
  onClose: close,
  builder,
  editMode,
  ruleId,
}: {
  isOpen: boolean;
  onClose: () => void;
  builder: AutoscaleRulesBuilderAPI;
  editMode?: boolean;
  ruleId?: string;
}) {
  const [showTable, setShowTable] = useState(false);
  const createRuleMutation = useCreateAutoscalingRule(editMode ? ruleId : undefined);

  const { activeTenantName, activeTenantId, accounts } = useAuthContext();

  const subAccounts = useMemo(() => {
    if (!activeTenantId || !accounts) {
      return [];
    }
    return accounts.filter((account) => account.parentAccountId === activeTenantId);
  }, [accounts, activeTenantId]);

  const hasValidCondition = builder.ruleConditions.some(
    (condition) =>
      condition.type &&
      condition.conditionOperators.some((op) => op.condition && op.value.length > 0)
  );

  const targetAccounts =
    builder.selectedAccountIds.length > 0
      ? builder.selectedAccountIds
      : subAccounts?.length > 0
        ? subAccounts.map((a) => a.accountId)
        : [];

  const handleNext = () => {
    if (!showTable && hasValidCondition) {
      setShowTable(true);
    }
  };

  const handleBack = () => setShowTable(false);

  const handleClose = () => {
    setShowTable(false);
    close();
  };
  const handleCreateAndApply = async () => {
    // If targetAccounts is empty, it means we are in a regular account, so we run once with no specific ID
    const runs = targetAccounts.length > 0 ? targetAccounts : [undefined];

    try {
      const responses = await Promise.all(
        runs.map((accountId) =>
          createRuleMutation.mutateAsync({
            conditions: builder.ruleConditions,
            active: true,
            accountId,
          })
        )
      );

      if (editMode) {
        toaster({
          title: 'Success',
          message: `Rule ${ruleId} has been successfully updated and applied.`,
          variant: 'success',
          toastId: ruleId,
        });
      } else if (runs.length > 1) {
        toaster({
          title: 'Success',
          message: `${runs.length} new rules have been successfully created and applied.`,
          variant: 'success',
        });
      } else {
        toaster({
          title: 'Success',
          message: `Rule ${responses[0].ruleId} has been successfully created and applied.`,
          variant: 'success',
          toastId: responses[0].ruleId,
        });
      }

      builder.resetBuilder();
      setShowTable(false);
      close();
    } catch (error) {
      console.error(
        editMode ? 'Error updating rule:' : 'Error creating and applying rules:',
        error
      );
      toaster({
        title: 'Error',
        message: editMode
          ? `Rule ${ruleId} failed to update. Please try again.`
          : 'Failed to create autoscaling rules. Please try again.',
        variant: 'error',
      });
    }
  };

  const handleCreate = async () => {
    const targetAccounts =
      builder.selectedAccountIds.length > 0
        ? builder.selectedAccountIds
        : subAccounts && subAccounts.length > 0
          ? subAccounts.map((a) => a.accountId)
          : [];

    const runs = targetAccounts.length > 0 ? targetAccounts : [undefined];

    try {
      const responses = await Promise.all(
        runs.map((accountId) =>
          createRuleMutation.mutateAsync({
            conditions: builder.ruleConditions,
            active: false,
            accountId,
          })
        )
      );

      if (editMode) {
        toaster({
          title: 'Success',
          message: `Rule ${ruleId} has been successfully updated.`,
          variant: 'success',
          toastId: ruleId,
        });
      } else if (runs.length > 1) {
        toaster({
          title: 'Success',
          message: `${runs.length} new rules have been successfully created.`,
          variant: 'success',
        });
      } else {
        toaster({
          title: 'Success',
          message: `Rule ${responses[0].ruleId} has been successfully created.`,
          variant: 'success',
          toastId: responses[0].ruleId,
        });
      }

      builder.resetBuilder();
      setShowTable(false);
      close();
    } catch (error) {
      console.error(editMode ? 'Error updating rule:' : 'Error creating rules:', error);
      toaster({
        title: 'Error',
        message: editMode
          ? `Rule ${ruleId} failed to update. Please try again.`
          : 'Failed to create autoscaling rules. Please try again.',
        variant: 'error',
      });
    }
  };

  return (
    <Dialog
      open={isOpen}
      as="div"
      className="relative z-[99] focus:outline-none"
      onClose={handleClose}
    >
      <div className="fixed inset-0 p-4 w-screen overflow-y-auto bg-[#00000059] backdrop-blur-[6px]">
        <div className="flex items-center justify-center p-8 h-full">
          <DialogPanel
            transition
            className="flex my-auto max-h-full h-full w-full max-w-5xl flex-col rounded-xl bg-white backdrop-blur-2xl duration-300 ease-out overflow-hidden data-closed:transform-[scale(95%)] data-closed:opacity-0"
          >
            <div className="flex flex-col gap-4 min-h-full p-6">
              <div className="py-2.5">
                <DialogHeader
                  title={editMode ? 'Edit Autoscaling Rule' : 'Create Autoscaling Rule'}
                  subtitles={
                    editMode && ruleId ? [activeTenantName, `${ruleId}`] : [activeTenantName]
                  }
                />
              </div>

              {!showTable ? (
                <div className="flex-1 flex flex-col min-h-0 overflow-y-auto side-panel-scrollbar mb-2 gap-6">
                  {/* Account Selector Section */}
                  {subAccounts?.length > 0 && (
                    <div className="flex flex-col gap-2 z-50">
                      <Typography
                        variant="p2"
                        fontWeight={500}
                        className="text-primary-900 font-family-inter"
                      >
                        Apply on
                      </Typography>
                      <AccountSelector
                        accounts={subAccounts}
                        selected={builder.selectedAccountIds}
                        onChange={builder.setSelectedAccountIds}
                        disabled={editMode}
                      />
                    </div>
                  )}

                  <div className="flex flex-col gap-2 z-10">
                    <AutoscaleRulesBuilder builder={builder} />
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex flex-col min-h-0">
                  <CreateAutoscaleRulesTables
                    conditions={builder.ruleConditions}
                    accountIds={targetAccounts}
                  />
                </div>
              )}

              <DialogFooter
                onCancel={handleClose}
                onNext={handleNext}
                onBack={showTable ? handleBack : undefined}
                nextDisabled={!hasValidCondition && !showTable}
                showTable={showTable}
                onCreateAndApply={handleCreateAndApply}
                onCreateAndNotApply={handleCreate}
                isCreating={createRuleMutation.isPending}
                editMode={editMode}
              />
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
function DialogHeader({ title, subtitles }: { title: string; subtitles: (string | undefined)[] }) {
  return (
    <div className="flex flex-col font-family-outfit">
      <DialogTitle as="h3" className="text-2xl 2xl:text-[28px] font-[500] ">
        {title}
      </DialogTitle>
      <div className="flex divide-x-2 divide-gray-400">
        {subtitles.map((subtitle, index) => (
          <Typography
            key={index}
            variant="p3"
            fontWeight={400}
            color="text-gray-700"
            className={index > 0 ? 'pl-2' : 'pr-2'}
          >
            {subtitle}
          </Typography>
        ))}
      </div>
    </div>
  );
}

function DialogFooter({
  onCancel,
  onNext,
  onBack,
  onCreateAndApply,
  onCreateAndNotApply,
  nextDisabled,
  showTable,
  isCreating,
  editMode,
}: {
  onCancel: () => void;
  onNext: () => void;
  onBack?: () => void;
  onCreateAndApply?: () => void;
  onCreateAndNotApply?: () => void;
  nextDisabled?: boolean;
  showTable?: boolean;
  isCreating?: boolean;
  editMode?: boolean;
}) {
  return (
    <div className="mt-auto flex justify-between gap-2 pt-6 px-6 pb-6 -ml-6 -mr-6 -mb-6 border-t border-primary-70 bg-white z-50">
      <div>
        {onBack && (
          <Button
            leftIcon={<Chevron className="text-primary-900 size-5 rotate-90" />}
            onClick={onBack}
            variant="third"
            disabled={isCreating}
          >
            Back
          </Button>
        )}
      </div>
      <div className="flex gap-2">
        <Button onClick={onCancel} variant="third" className="px-5" disabled={isCreating}>
          Cancel
        </Button>
        {!showTable && (
          <Button onClick={onNext} disabled={nextDisabled} className="px-7">
            Next
          </Button>
        )}
        {showTable && onCreateAndNotApply && (
          <Button
            onClick={onCreateAndNotApply}
            variant="third"
            disabled={nextDisabled || isCreating}
            className="px-7.5"
          >
            {isCreating ? (editMode ? 'Updating...' : 'Creating...') : editMode ? 'Edit' : 'Create'}
          </Button>
        )}
        {showTable && onCreateAndApply && (
          <Button onClick={onCreateAndApply} disabled={nextDisabled || isCreating} className="px-7">
            {isCreating
              ? editMode
                ? 'Updating...'
                : 'Creating...'
              : editMode
                ? 'Edit and Apply'
                : 'Create and Apply'}
          </Button>
        )}
      </div>
    </div>
  );
}

function AccountSelector({
  accounts,
  selected,
  onChange,
  disabled = false,
}: {
  accounts: Account[];
  selected: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
}) {
  return (
    <AccountInputMultiSelect
      accounts={accounts}
      values={selected}
      onChange={onChange}
      disabled={disabled}
      className="w-full"
    />
  );
}
